/**
 * Event Types System for Vietnamese Cultural Events
 * 8 event types: Lễ Tết, Giỗ, Cưới Hỏi, Ma Chay, Sinh Nhật, Kỷ Niệm, Công Việc, Khác
 */

export enum EventType {
    LE_TET = 'le_tet',
    GIO = 'gio',
    SU_KIEN = 'su_kien',
    MA_CHAY = 'ma_chay',
    SINH_NHAT = 'sinh_nhat',
    KY_NIEM = 'ky_niem',
    CONG_VIEC = 'cong_viec',
    KHAM_BENH = 'kham_benh',
    CHI_TIEU = 'chi_tieu',
    KHAC = 'khac',
}

export interface EventField {
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'number' | 'select' | 'date' | 'checkbox';
    required?: boolean;
    placeholder?: string;
    options?: string[];
    defaultValue?: unknown;
}

export interface EventTypeConfig {
    id: EventType;
    label: string;
    labelEn: string;
    icon: string;
    color: string;
    bgColor: string;
    description: string;
    fields: EventField[];
    defaultReminders: number[];
}

export const EVENT_TYPE_CONFIGS: Record<EventType, EventTypeConfig> = {
    [EventType.LE_TET]: {
        id: EventType.LE_TET,
        label: 'Lễ Tết',
        labelEn: 'Holiday/Festival',
        icon: '🏮',
        color: 'text-red-600',
        bgColor: 'bg-gray-50',
        description: 'Các ngày lễ, tết truyền thống',
        fields: [
            {
                name: 'festivalName',
                label: 'Tên lễ',
                type: 'select',
                required: true,
                options: [
                    'Tết Nguyên Đán',
                    'Tết Trung Thu',
                    'Tết Đoan Ngọ',
                    'Rằm tháng Giêng',
                    'Rằm tháng 7',
                    'Tết Hàn Thực',
                    'Vu Lan',
                ],
            },
            {
                name: 'preparations',
                label: 'Chuẩn bị',
                type: 'textarea',
                placeholder: 'Cần mua gì, chuẩn bị gì...',
            },
            {
                name: 'location',
                label: 'Địa điểm',
                type: 'text',
                placeholder: 'Nhà, chùa, đình...',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.GIO]: {
        id: EventType.GIO,
        label: 'Giỗ',
        labelEn: 'Death Anniversary',
        icon: '🕯️',
        color: 'text-gray-700',
        bgColor: 'bg-gray-50',
        description: 'Ngày giỗ người thân',
        fields: [
            {
                name: 'deathYear',
                label: 'Năm mất',
                type: 'number',
                placeholder: 'VD: 2020',
            },
            {
                name: 'location',
                label: 'Địa điểm tổ chức',
                type: 'text',
                placeholder: 'Nhà riêng, chùa...',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.SU_KIEN]: {
        id: EventType.SU_KIEN,
        label: 'Sự Kiện',
        labelEn: 'Upcoming Event',
        icon: '🎉',
        color: 'text-pink-600',
        bgColor: 'bg-gray-50',
        description: 'Đám cưới, tiệc, sự kiện sắp tới',
        fields: [
            {
                name: 'eventName',
                label: 'Loại sự kiện',
                type: 'select',
                options: ['Đám cưới', 'Tiệc sinh nhật', 'Tiệc công ty', 'Họp mặt', 'Khác'],
            },
            {
                name: 'location',
                label: 'Địa điểm',
                type: 'text',
                placeholder: 'Nhà hàng, địa chỉ...',
            },
            {
                name: 'giftAmount',
                label: 'Quà/Mừng (VNĐ)',
                type: 'number',
                placeholder: 'Số tiền',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.MA_CHAY]: {
        id: EventType.MA_CHAY,
        label: 'Ma Chay',
        labelEn: 'Funeral',
        icon: '🖤',
        color: 'text-gray-900',
        bgColor: 'bg-gray-50',
        description: 'Tang lễ, lễ cúng',
        fields: [
            {
                name: 'ceremonyType',
                label: 'Loại lễ',
                type: 'select',
                options: ['Tang lễ', 'Lễ 49 ngày', 'Lễ 100 ngày', 'Giỗ đầu', 'Khác'],
            },
            {
                name: 'location',
                label: 'Địa điểm',
                type: 'text',
                required: true,
            },
            {
                name: 'condolenceMoney',
                label: 'Phúng điếu (VNĐ)',
                type: 'number',
                placeholder: 'Số tiền',
            },
        ],
        defaultReminders: [3, 1],
    },

    [EventType.SINH_NHAT]: {
        id: EventType.SINH_NHAT,
        label: 'Sinh Nhật',
        labelEn: 'Birthday',
        icon: '🎂',
        color: 'text-yellow-600',
        bgColor: 'bg-gray-50',
        description: 'Sinh nhật người thân, bạn bè',
        fields: [
            {
                name: 'relationship',
                label: 'Quan hệ',
                type: 'text',
            },
            {
                name: 'lunarBirthday',
                label: 'Sinh nhật Âm lịch',
                type: 'checkbox',
                defaultValue: false,
            },
            {
                name: 'age',
                label: 'Tuổi',
                type: 'number',
            },
            {
                name: 'gift',
                label: 'Quà tặng',
                type: 'text',
                placeholder: 'Ý tưởng quà...',
            },
        ],
        defaultReminders: [7, 1],
    },

    [EventType.KY_NIEM]: {
        id: EventType.KY_NIEM,
        label: 'Kỷ Niệm',
        labelEn: 'Anniversary',
        icon: '💝',
        color: 'text-purple-600',
        bgColor: 'bg-gray-50',
        description: 'Ngày kỷ niệm đặc biệt',
        fields: [
            {
                name: 'yearsSince',
                label: 'Năm thứ',
                type: 'number',
            },
            {
                name: 'plans',
                label: 'Kế hoạch',
                type: 'textarea',
                placeholder: 'Đi ăn, du lịch...',
            },
        ],
        defaultReminders: [7, 3, 1],
    },

    [EventType.CONG_VIEC]: {
        id: EventType.CONG_VIEC,
        label: 'Công Việc',
        labelEn: 'Work',
        icon: '💼',
        color: 'text-blue-600',
        bgColor: 'bg-gray-50',
        description: 'Sự kiện công việc',
        fields: [
            {
                name: 'priority',
                label: 'Độ ưu tiên',
                type: 'select',
                options: ['Cao', 'Trung bình', 'Thấp'],
            },
            {
                name: 'description',
                label: 'Mô tả',
                type: 'textarea',
            },
        ],
        defaultReminders: [1],
    },

    [EventType.KHAM_BENH]: {
        id: EventType.KHAM_BENH,
        label: 'Khám Bệnh',
        labelEn: 'Medical',
        icon: '🏥',
        color: 'text-red-600',
        bgColor: 'bg-gray-50',
        description: 'Lịch khám bệnh, tái khám',
        fields: [
            {
                name: 'facility',
                label: 'Cơ sở y tế',
                type: 'text',
                placeholder: 'Bệnh viện, phòng khám...',
            },
            {
                name: 'doctor',
                label: 'Bác sĩ',
                type: 'text',
            },
            {
                name: 'reason',
                label: 'Lý do khám',
                type: 'text',
                placeholder: 'Tái khám, kiểm tra sức khỏe...',
            },
            {
                name: 'notes',
                label: 'Ghi chú',
                type: 'textarea',
            },
        ],
        defaultReminders: [3, 1],
    },

    [EventType.CHI_TIEU]: {
        id: EventType.CHI_TIEU,
        label: 'Chi Tiêu',
        labelEn: 'Expense',
        icon: '💸',
        color: 'text-emerald-600',
        bgColor: 'bg-gray-50',
        description: 'Theo dõi chi tiêu',
        fields: [
            {
                name: 'category',
                label: 'Danh mục',
                type: 'select',
                options: ['Ăn uống', 'Mua sắm', 'Đi lại', 'Hóa đơn', 'Giải trí', 'Sức khỏe', 'Giáo dục', 'Khác'],
            },
            {
                name: 'amount',
                label: 'Số tiền (VNĐ)',
                type: 'number',
                required: true,
            },
            {
                name: 'paymentMethod',
                label: 'Hình thức',
                type: 'select',
                options: ['Tiền mặt', 'Chuyển khoản', 'Thẻ tín dụng', 'Ví điện tử'],
            },
            {
                name: 'notes',
                label: 'Ghi chú',
                type: 'textarea',
            },
        ],
        defaultReminders: [],
    },

    [EventType.KHAC]: {
        id: EventType.KHAC,
        label: 'Khác',
        labelEn: 'Other',
        icon: '📝',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        description: 'Sự kiện khác',
        fields: [],
        defaultReminders: [1],
    },
};

// Helper functions
export const getEventTypeConfig = (type: EventType): EventTypeConfig => {
    return EVENT_TYPE_CONFIGS[type];
};

export const getEventTypeLabel = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.label || 'Khác';
};

export const getEventTypeIcon = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.icon || '📝';
};

export const getEventTypeColor = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.color || 'text-gray-600';
};

export const getEventTypeBgColor = (type: EventType): string => {
    return EVENT_TYPE_CONFIGS[type]?.bgColor || 'bg-gray-50';
};

export const getAllEventTypes = (): EventType[] => {
    return Object.values(EventType);
};
